import type { ComputedRef } from 'vue'
import { findMacroEnd } from '../model/macros'
import type { FlowNode, FlowNodeData, MacroOption } from '../model/types'
import type { FlowDocument } from './useFlowDocument'
import type { FlowHistory } from './useFlowHistory'
import type { FlowSelection } from './useFlowSelection'

type Deps = {
  document: FlowDocument
  selection: FlowSelection
  history: FlowHistory
  macroOptions: ComputedRef<MacroOption[]>
}

const asString = (v: string) => v || undefined
const asNumber = (v: string) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : undefined
}
const portCount = (v: string) => Math.max(1, Math.min(8, parseInt(v) || 1))

const INPUT_DEBOUNCE_MS = 150

/**
 * Edits of the selected node from the inspector. Every edit is one undoable history
 * entry; text inputs are debounced and flushed on blur.
 */
export function useNodeInspector({ document, selection, history, macroOptions }: Deps) {
  /** Applies `mutate` to the node selected at the time the edit is committed. */
  const editSelected = (
    label: string,
    mutate: (node: FlowNode) => void,
    guard?: (node: FlowNode) => boolean,
  ) => {
    const node = selection.selectedNode.value
    if (!node || (guard && !guard(node))) return
    history.runMutation(label, () => mutate(node))
  }

  const fieldUpdater =
    <K extends keyof FlowNodeData>(key: K, label: string, coerce: (v: string) => FlowNodeData[K]) =>
    (value: string) =>
      editSelected(label, (node) => {
        node.data[key] = coerce(value)
      })

  const setter =
    <K extends keyof FlowNodeData>(key: K, label: string) =>
    (value: FlowNodeData[K]) =>
      editSelected(label, (node) => {
        node.data[key] = value
      })

  /** Re-applies port counts to every macro-call that references `macroRef`. */
  const syncMacroCallPorts = (macroRef: string) => {
    const option = macroOptions.value.find((macro) => macro.name === macroRef)
    for (const n of document.nodes.value) {
      if (n.type === 'macro-call' && n.data.macroRef === macroRef) {
        n.data.inputCount = option?.inputCount ?? 1
        n.data.outputCount = option?.outputCount ?? 1
      }
    }
  }

  const updateNodeLabel = (value: string) => {
    const node = selection.selectedNode.value
    if (!node || node.label === value) return
    history.runMutation('Rename node', () => {
      node.label = value
    })
  }

  const updatePhaseId = (value: string) =>
    editSelected(
      'Update phase id',
      (node) => {
        node.data.phaseId = value
      },
      (node) => node.type === 'phase' && (node.data.phaseId || '') !== value,
    )

  const updateGroupId = (value: string) =>
    editSelected(
      'Update group',
      (node) => {
        node.data.groupId = value
      },
      (node) => node.type === 'group' && node.data.groupId !== value,
    )

  const updateMacroName = (value: string) =>
    editSelected(
      'Update macro name',
      (node) => {
        node.data.macroName = value.trim()
      },
      (node) => node.type === 'macro-start',
    )

  const updateMacroInputCount = (value: string) =>
    editSelected(
      'Update macro input count',
      (node) => {
        node.data.inputCount = portCount(value)
        if (node.data.macroName) syncMacroCallPorts(node.data.macroName)
      },
      (node) => node.type === 'macro-start',
    )

  const updateMacroOutputCount = (value: string) =>
    editSelected(
      'Update macro output count',
      (node) => {
        node.data.outputCount = portCount(value)
        // Propagate to calls of the macro whose body ends at this macro-end.
        const graph = { nodes: document.nodes.value, edges: document.edges.value }
        const owner = document.nodes.value.find(
          (start) =>
            start.type === 'macro-start' &&
            start.data.macroName &&
            findMacroEnd(graph, start.id)?.id === node.id,
        )
        if (owner) syncMacroCallPorts(owner.data.macroName as string)
      },
      (node) => node.type === 'macro-end',
    )

  const updateMacroRef = (value: string) =>
    editSelected(
      'Update macro ref',
      (node) => {
        node.data.macroRef = value
        const option = macroOptions.value.find((macro) => macro.name === value)
        node.data.inputCount = option?.inputCount ?? 1
        node.data.outputCount = option?.outputCount ?? 1
      },
      (node) => node.type === 'macro-call',
    )

  const updateAction = (value: string) =>
    editSelected(
      'Update action',
      (node) => {
        node.data.actionType = value
      },
      (node) => node.type === 'action' && node.data.actionType !== value,
    )

  // A single timer is shared by all fields, like a form-wide autosave.
  let debounceTimer: ReturnType<typeof setTimeout> | null = null
  /** Wraps an @input handler so that it commits 150 ms after typing stops. */
  const debounced = (commit: (v: string) => void) => (event: Event) => {
    const value = (event.target as HTMLInputElement).value
    if (debounceTimer !== null) clearTimeout(debounceTimer)
    debounceTimer = setTimeout(() => {
      commit(value)
      debounceTimer = null
    }, INPUT_DEBOUNCE_MS)
  }
  /** @blur handler: drops a pending debounce and commits immediately. */
  const flush = (commit: (v: string) => void) => (event: Event) => {
    const value = (event.target as HTMLInputElement).value
    if (debounceTimer !== null) {
      clearTimeout(debounceTimer)
      debounceTimer = null
    }
    commit(value)
  }

  return {
    debounced,
    flush,
    updateNodeLabel,
    updatePhaseId,
    updateGroupId,
    updateMacroName,
    updateMacroInputCount,
    updateMacroOutputCount,
    updateMacroRef,
    updateAction,
    updateCondLeft: fieldUpdater('condLeft', 'Update condition left', asString),
    updateCondLeftLit: fieldUpdater('condLeftLit', 'Update condition left literal', asNumber),
    updateCondOp: fieldUpdater('condOp', 'Update condition operator', asString),
    updateCondRight: fieldUpdater('condRight', 'Update condition right', asString),
    updateCondRightLit: fieldUpdater('condRightLit', 'Update condition right literal', asNumber),
    updatePullDirection: fieldUpdater('pullDirection', 'Update pull direction', asString),
    updatePullAnimation: fieldUpdater('pullAnimation', 'Update pull animation', asString),
    updateCheckboxCount: fieldUpdater('checkboxCount', 'Update checkbox count', asNumber),
    updateCheckboxDelayMs: fieldUpdater('checkboxDelayMs', 'Update checkbox delay', asNumber),
    updateWaitDelayMs: fieldUpdater('waitDelayMs', 'Update wait delay', asNumber),
    updateDelayMs: fieldUpdater('delayMs', 'Update delay', asNumber),
    updateAudioElementIndex: fieldUpdater(
      'audioElementIndex',
      'Update audio element index',
      asNumber,
    ),
    updateWaitForAudio: setter('waitForAudio', 'Update wait for audio'),
    updateMediaType: setter('mediaType', 'Update media type'),
    updateMediaTypeRight: setter('mediaTypeRight', 'Update right media type'),
    updateDisplayMode: setter('displayMode', 'Update display mode'),
    updateSplitSwap: setter('splitSwap', 'Update split swap'),
    updateGetSource: fieldUpdater('getSource', 'Update get source', asString),
    updateGetRegister: fieldUpdater('getRegister', 'Update get register', asString),
    updateGetLit: fieldUpdater('getLit', 'Update get literal', asNumber),
    updateGetPhase: fieldUpdater('getPhase', 'Update get phase', asString),
    updateSetTarget: fieldUpdater('setTarget', 'Update set target', asString),
    updateSetInput: fieldUpdater('setInput', 'Update set input', asString),
    updateSetLit: fieldUpdater('setLit', 'Update set literal', asNumber),
    updateMathTarget: fieldUpdater('mathTarget', 'Update math target', asString),
    updateMathOp: fieldUpdater('mathOp', 'Update math op', asString),
    updateMathOperand: fieldUpdater('mathOperand', 'Update math operand', asString),
    updateMathLit: fieldUpdater('mathLit', 'Update math literal', asNumber),
  }
}

export type NodeInspector = ReturnType<typeof useNodeInspector>
